import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Dict, NotificationService, ServerRootService } from '@se/se.ui.html5';
import { firstValueFrom, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CollectionSearchGrid, UserConfiguration } from '../models/estensa-models.model';
import { selectSeSearchSelectedItemIds, selectSeSearchSelectedItems } from '../root-store/root.selectors';
import { RootState } from '../root-store/root.state';
import { selectSeButtonsToolbarButtons } from '../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.selectors';
import { EstensaCollectionService } from '../services/angularjs/estensa-collection.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
import { orderCollectionByStringProp } from '../utils/utils';
import { nreSearchParams } from './mock-search-params/nre';
import { telemonitoringSearchParams } from './mock-search-params/telemonitoring';
import { paramsDateReviver } from './search-page-routines';
import { SearchFormType } from './search-page-type.enum';
import { SearchParam, SearchParams } from './search-param.model';
import { executionSiteFilterDefs, executionUserFilterDefs } from './search-page-filter-defs/execution-filter.defs';

@Injectable({
    providedIn: 'root',
})
export class SearchPageService extends ServerRootService {
    private params: Dict<SearchParams> = {};

    // constructor
    constructor(
        private collectionService: EstensaCollectionService,
        private httpClient: HttpClient,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private radioSettingsService: RadioSettingsService,
        private store: Store<RootState>,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
    ) {
        super();

        this.initService();
    }

    getResult(formType: SearchFormType, searchInfo: { searchType: any }): Observable<any> {
        const params = JSON.stringify(searchInfo);
        switch (formType) {
            case SearchFormType.ECG:
                return this.httpClient.post(this.getServerRoot() + '/ECG/GetEcgWorklist', params, this.buildOptions());
            case SearchFormType.VISIT:
                return this.httpClient.post(
                    this.getServerRoot() + '/Visit/GetVisitWorklist',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.COMPLETE_CD:
                return this.httpClient.post(
                    this.getServerRoot() + '/MPS/RetrieveCompletedListEx',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.ON_PRINTING_CD:
                return this.httpClient.post(
                    this.getServerRoot() + '/MPS/RetrieveQueueFilteredEx',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.TELEMONITORING:
                return this.httpClient.post(
                    this.getServerRoot() + '/HealthPlatform/GetHealthPlatformWorklist',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.NRE:
                return this.httpClient.post(
                    this.getServerRoot() + '/NRE/GetWorklistForNRE',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.DUMMY_VNEXT2:
                return this.httpClient.post(
                    this.getServerRoot() + '/Visit/GetVisitWorklist',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.EXECUTION:
                return this.httpClient.post(
                    this.getServerRoot() + '/Worklist/GetExecutionWorklist',
                    params,
                    this.buildOptions(),
                );
            case SearchFormType.SPECIALTY:
                /*GetAllWorklist, GetWorklistCPRadioReporting, GetWorklistCPRadioReported, GetWorklistCPRadioApproved
                 * se ci sara un searchType diverso non va passato nei parametri ma vanno chiamati endpoint diversi*/
                const endPointRest = this.getSpecialtyEndPointBySearchType(searchInfo.searchType);
                return this.httpClient.post(
                    this.getServerRoot() + `/Worklist/${endPointRest}`,
                    params,
                    this.buildOptions(),
                );
            default:
                return of(null);
        }
    }

    getSpecialtyEndPointBySearchType = (searchType: number | null) => {
        switch (searchType) {
            case 0:
                return 'GetAllWorklist';
            case 1:
                return 'GetWorklistCPRadioReporting';
            case 2:
                return 'GetWorklistCPRadioReported';
            case 3:
                return 'GetWorklistCPRadioApproved';
            default:
                return 'GetAllWorklist';
        }
    };

    getSearchParams(form: SearchFormType): SearchParams {
        return this.params[form];
    }

    initExecutionFilters() {
        const deserializedExecutionSearchFilters: SearchParam[] = this.getExecutionFilterFromSettings();
        this.params[SearchFormType.EXECUTION] = new SearchParams(deserializedExecutionSearchFilters);
    }

    async getSelectedItemIds(formType: SearchFormType): Promise<any> {
        return firstValueFrom(this.store.pipe(select(selectSeSearchSelectedItemIds(formType))));
    }

    async getSelectedItems(formType: SearchFormType, idPropertyName: string): Promise<any[]> {
        return firstValueFrom(this.getSelectedItems$(formType, idPropertyName));
    }

    getSelectedItems$(formType: SearchFormType, idPropertyName: string) {
        return this.store.pipe(select(selectSeSearchSelectedItems(formType, idPropertyName)));
    }

    getToolbarButtons() {
        return this.store.pipe(select(selectSeButtonsToolbarButtons));
    }

    getAssociatedVisits(idReport: string) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/GetReportExaminations',
            { idReport },
            this.buildOptions(),
        );
    }

    // Carico le collection
    loadFormCollections(form: SearchFormType) {
        Object.entries(this.params[form].dataItemsSource).forEach(([name, itemsSource]) => {
            if (!!itemsSource.controllerName) {
                this.collectionService
                    .get(itemsSource.controllerName as any, itemsSource.methodName)
                    .pipe(
                        finalize(() =>
                            setTimeout(() => {
                                this.params[form].endDataLoading(name);
                            }),
                        ),
                    )
                    .subscribe({
                        next:
                            itemsSource.sortCollectionDataInfo && itemsSource.sortCollectionDataInfo.field
                                ? (data) =>
                                      this.params[form].addData(
                                          name,
                                          orderCollectionByStringProp(data, itemsSource.sortCollectionDataInfo.field),
                                      )
                                : (data) => this.params[form].addData(name, data),
                        error: (e) =>
                            this.notificationService.error(
                                e.message ? e.message : '',
                                this.translate.instant('ErrorLoadingData'),
                            ),
                    });
            } else {
                /*Collection locale*/
                this.getFromLocal(itemsSource.methodName)
                    .pipe(
                        finalize(() =>
                            setTimeout(() => {
                                this.params[form].endDataLoading(name);
                            }),
                        ),
                    )
                    .subscribe({
                        next: (data) => this.params[form].addData(name, data),
                        error: (e) =>
                            this.notificationService.error(
                                e.message ? e.message : '',
                                this.translate.instant('ErrorLoadingData'),
                            ),
                    });
            }
        });
    }

    /**
   IDLE = 1,
   PENDING = 2,
   CREATING = 3,
   DONE = 4,
   FAILED = 5,
   BLOCKED = 6,
   ABORTED = 7,
   WAITING = 8,
   BURNER = 9,
   UNKNOWN = 10
   */

    private getFromLocal(methodName: string) {
        switch (methodName) {
            // Liste caricate all'avvio.
            case 'GetMpsCdCompletedStatus':
                return of([
                    { id: 'ALL', description: this.translate.instant('All') },
                    { id: 'DONE', description: this.translate.instant('MPSJobsSuccedeed') },
                    { id: 'FAILED', description: this.translate.instant('MPSJobsFailed') },
                    { id: 'ABORTED', description: this.translate.instant('MPSJobsCancelled') },
                ]);

            case 'GetMpsOnPrintingCdStatus':
                return of([
                    { id: 'ALL', description: this.translate.instant('All') },
                    { id: 'IDLE', description: 'IDLE' },
                    { id: 'PENDING', description: 'PENDING' },
                    { id: 'CREATING', description: 'CREATING' },
                    { id: 'DONE', description: 'DONE' },
                    { id: 'FAILED', description: 'FAILED' },
                    { id: 'BLOCKED', description: 'BLOCKED' },
                    { id: 'ABORTED', description: 'ABORTED' },
                    { id: 'WAITING', description: 'WAITING' },
                    { id: 'BURNER', description: 'BURNER' },
                    { id: 'UNKNOWN', description: 'UNKNOWN' },
                ]);

            default:
                return of([]);
        }
    }

    private getFavoriteFilterValues(favorites: CollectionSearchGrid[]) {
        if (favorites) {
            const defaultFavorite = favorites.filter((favorite) => favorite.isDefault)[0];
            if (defaultFavorite) {
                return JSON.parse(defaultFavorite.searchParams);
            }
        }
        return {};
    }

    public calculateParamDate(delta: number) {
        const todayMidnight = new Date();
        todayMidnight.setHours(23, 59, 59, 0);
        const from = new Date();
        from.setDate(from.getDate() - (delta - 1));
        return { from, to: todayMidnight };
    }

    private loadFavoriteFilterValues(filterValues: any, deserializedSearchParams: any) {
        if (Object.keys(filterValues).length >= 1) {
            deserializedSearchParams.forEach((field: any) => {
                if (filterValues.hasOwnProperty(field.property.name)) {
                    field.property.value = filterValues[field.property.name];
                }
                if (field.property.name === 'date' && !!filterValues['dateDelta']) {
                    field.property.value = this.calculateParamDate(filterValues['dateDelta']);
                }
            });
        }
        return deserializedSearchParams;
    }

    private initService() {
        // Lettura dei settings
        const settings = this.radioSettingsService.radioSettings;
        const userSettings: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;

        // ECG
        let deserializedEcgSearchParams: SearchParam[] = JSON.parse(
            settings.ecgWorklistSettings.searchParamDefs,
            this.reviver,
        );
        const ecgFavorites =
            userSettings?.preferences.searchGridUserPreference?.ecgWorklistSearchGrid?.collection ?? [];
        const ecgFilterValues = this.getFavoriteFilterValues(ecgFavorites || []);
        deserializedEcgSearchParams = this.loadFavoriteFilterValues(ecgFilterValues, deserializedEcgSearchParams);
        //const deserializedEcgSearchParams: SearchParam[] = JSON.parse(
        //    '[ { "active": true, "property": { "name": "surname", "value": "", "type": "string" }, "labelTranslationKey": "LastName", "layout": { "row": 0, "column": 0, "autofocus": true, "stretch": true } }, { "active": true, "property": { "name": "name", "value": "", "type": "string" }, "labelTranslationKey": "FirstName", "layout": { "row": 0, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "birthDate", "value": "", "defaultValue": "", "type": "date" }, "labelTranslationKey": "BirthDate", "layout": { "row": 1, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idSeverity", "value": null, "type": "select", "itemsSourceInfo": { "form": "ecg", "controllerName": "RadiologyCommon", "methodName": "GetSeverities", "enableTooltip": true } }, "labelTranslationKey": "Severity", "layout": { "row": 2, "column": 0, "stretch": true } }, { "active": false, "property": { "name": "idProvenance", "value": null, "type": "select", "itemsSourceInfo": { "form": "ecg", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenance", "layout": { "row": 2, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "idProvenances", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "ecg", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenances", "layout": { "row": 2, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "idBusinessUnits", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "ecg", "controllerName": "Html5", "methodName": "GetBusinessUnits", "enableTooltip": true } }, "labelTranslationKey": "BusinessUnit", "layout": { "row": 3, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idEquipments", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "ecg", "controllerName": "List", "methodName": "GetEquipments", "enableTooltip": true } }, "labelTranslationKey": "Equipment", "layout": { "row": 3, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "date", "value": { "from": "0#-2#0", "to": "0#0#0" }, "defaultValue": { "from": "0#-2#0", "to": "0#0#0" }, "type": "fromTo" }, "labelTranslationKey": { "from": "From", "to": "To", "label": "ExamDate" }, "layout": { "row": 4, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idReferringPhysician", "value": null, "type": "select", "itemsSourceInfo": { "form": "ecg", "controllerName": "List", "methodName": "GetReferringPhysicians", "displayProperty": "description", "enableTooltip": true, "sortCollectionDataInfo": { "field": "fullName" } } }, "labelTranslationKey": "ReferringPhysician", "layout": { "row": 5, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "examNumber", "value": "", "type": "string" }, "labelTranslationKey": "ExamNumber", "layout": { "row": 5, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "patientId", "value": "", "type": "string" }, "labelTranslationKey": "FormularyNumber", "layout": { "row": 6, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "otherPatientId", "value": "", "type": "string" }, "labelTranslationKey": "OtherPatientId", "layout": { "row": 6, "column": 1, "stretch": true } } ]',
        //    this.reviver
        //);

        // VISIT
        let deserializedVisitSearchParams: SearchParam[] = JSON.parse(
            settings.visitWorklistSettings.searchParamDefs,
            this.reviver,
        );
        const visitFavorites =
            userSettings?.preferences.searchGridUserPreference?.visitWorklistSearchGrid?.collection ?? [];
        const visitFilterValues = this.getFavoriteFilterValues(visitFavorites);
        deserializedVisitSearchParams = this.loadFavoriteFilterValues(visitFilterValues, deserializedVisitSearchParams);
        //const deserializedVisitSearchParams: SearchParam[] = JSON.parse(
        //    '[ { "active": true, "property": { "name": "surname", "value": "", "type": "string" }, "labelTranslationKey": "LastName", "layout": { "row": 0, "column": 0, "autofocus": true, "stretch": true } }, { "active": true, "property": { "name": "name", "value": "", "type": "string" }, "labelTranslationKey": "FirstName", "layout": { "row": 0, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "birthDate", "value": "", "defaultValue": "", "type": "date" }, "labelTranslationKey": "BirthDate", "layout": { "row": 1, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idSeverity", "value": null, "type": "select", "itemsSourceInfo": { "form": "visit", "controllerName": "RadiologyCommon", "methodName": "GetSeverities", "enableTooltip": true } }, "labelTranslationKey": "Severity", "layout": { "row": 2, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idProvenances", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenances", "layout": { "row": 2, "column": 1, "stretch": true } }, { "active": false, "property": { "name": "idProvenance", "value": null, "type": "select", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenance", "layout": { "row": 2, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "idBusinessUnits", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetBusinessUnits", "enableTooltip": true } }, "labelTranslationKey": "BusinessUnit", "layout": { "row": 3, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idExams", "value": null, "type": "filterMultiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "List", "methodName": "GetExams", "displayProperty": "description", "enableTooltip": true, "sortCollectionDataInfo": { "field": "description" } } }, "labelTranslationKey": "ExecutedExams", "layout": { "row": 3, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "date", "value": { "from": "0#-2#0", "to": "0#0#0" }, "defaultValue": { "from": "0#-2#0", "to": "0#0#0" }, "type": "fromTo" }, "labelTranslationKey": { "from": "From", "to": "To", "label": "ExamDate" }, "layout": { "row": 4, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idReferringPhysician", "value": null, "type": "filterSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "List", "methodName": "GetReferringPhysicians", "displayProperty": "description", "enableTooltip": true, "sortCollectionDataInfo": { "field": "description" } } }, "labelTranslationKey": "ReferringPhysician", "layout": { "row": 5, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "examNumber", "value": "", "type": "string" }, "labelTranslationKey": "ExamNumber", "layout": { "row": 5, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "patientId", "value": "", "type": "string" }, "labelTranslationKey": "PatientId", "layout": { "row": 6, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "otherPatientId", "value": "", "type": "string" }, "labelTranslationKey": "OtherPatientId", "layout": { "row": 6, "column": 1, "stretch": true } } ]',
        //    this.reviver
        //);

        // CompleteCD
        const deserializedCompleteCdSearchParams: SearchParam[] = JSON.parse(
            settings.completeCdWorklistSettings.searchParamDefs,
            this.reviver,
        );
        //const deserializedCompleteCdSearchParams: SearchParam[] = JSON.parse(
        //    '[ { "active": true, "property": { "name": "patientName", "value": "", "type": "string" }, "labelTranslationKey": "PatientNameStr", "layout": { "row": 0, "column": 0, "autofocus": true, "stretch": true } }, { "active": true, "property": { "name": "status", "defaultValue": "ALL", "value": "ALL", "type": "select", "itemsSourceInfo": { "form": "complete_cd", "controllerName": null , "methodName": "GetMpsCdCompletedStatus", "displayProperty": "description", "enableTooltip": true } }, "labelTranslationKey": "Status", "layout": { "row": 1, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "studyDescription", "value": "", "type": "string" }, "labelTranslationKey": "StudyDescription", "layout": { "row": 2, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "executionDate", "value": { "from": "0#-2#0", "to": "0#0#0" }, "defaultValue": { "from": "0#-2#0", "to": "0#0#0" }, "type": "fromTo" }, "labelTranslationKey": { "from": "From", "to": "To", "label": "MPSExecutionDate" }, "layout": { "row": 3, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "description", "value": "", "type": "string" }, "labelTranslationKey": "Description", "layout": { "row": 4, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "burnerDescription", "value": "", "type": "string" }, "labelTranslationKey": "BurnerDescription", "layout": { "row": 5, "column": 0, "stretch": true } } ]',
        //    this.reviver);

        // OnPrinting
        const deserializedOnPrintingCdSearchParams: SearchParam[] = JSON.parse(
            settings.onPrintingCdWorklistSettings.searchParamDefs,
            this.reviver,
        );
        //const deserializedOnPrintingCdSearchParams: SearchParam[] = JSON.parse(
        //    '[ { "active": true, "property": { "name": "exam", "value": "", "type": "string" }, "labelTranslationKey": "Exam", "layout": { "row": 0, "column": 0, "autofocus": true, "stretch": true } }, { "active": true, "property": { "name": "server", "value": "", "type": "string" }, "labelTranslationKey": "MPSServer", "layout": { "row": 1, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "burner", "value": "", "type": "string" }, "labelTranslationKey": "MPSBurner", "layout": { "row": 2, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "status", "defaultValue": "ALL", "value": "ALL", "type": "select", "itemsSourceInfo": { "disableResetButton": true, "form": "on_printing_cd", 	"controllerName": null, 	"methodName": "GetMpsOnPrintingCdStatus", 	"displayProperty": "description", 	"enableTooltip": true } }, "labelTranslationKey": "Status", "layout": { "row": 3, "column": 0, "stretch": true } } ]',
        //    this.reviver);

        // Telemonitoring
        const deserializedTelemonitoringSearchParams: SearchParam[] = JSON.parse(
            telemonitoringSearchParams,
            this.reviver,
        );
        //'[ { "active": true, "property": { "name": "surname", "value": "", "type": "string" }, "labelTranslationKey": "LastName", "layout": { "row": 0, "column": 0, "autofocus": true, "stretch": true } }, { "active": true, "property": { "name": "name", "value": "", "type": "string" }, "labelTranslationKey": "FirstName", "layout": { "row": 0, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "idSeverity", "value": null, "type": "select", "itemsSourceInfo": { "form": "visit", "controllerName": "RadiologyCommon", "methodName": "GetSeverities", "enableTooltip": true } }, "labelTranslationKey": "Severity", "layout": { "row": 1, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idProvenances", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenances", "layout": { "row": 1, "column": 1, "stretch": true } }, { "active": false, "property": { "name": "idProvenance", "value": null, "type": "select", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetAllProvenances", "enableTooltip": true } }, "labelTranslationKey": "Provenance", "layout": { "row": 1, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "idBusinessUnits", "value": [], "type": "multiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "Html5", "methodName": "GetBusinessUnits", "enableTooltip": true } }, "labelTranslationKey": "BusinessUnit", "layout": { "row": 2, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idExams", "value": null, "type": "filterMultiSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "List", "methodName": "GetExams", "displayProperty": "description", "enableTooltip": true, "sortCollectionDataInfo": { "field": "description" } } }, "labelTranslationKey": "ExecutedExams", "layout": { "row": 2, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "date", "value": { "from": "0#-2#0", "to": "0#0#0" }, "defaultValue": { "from": "0#-2#0", "to": "0#0#0" }, "type": "fromTo" }, "labelTranslationKey": { "from": "From", "to": "To", "label": "ExamDate" }, "layout": { "row": 3, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "idReferringPhysician", "value": null, "type": "filterSelect", "itemsSourceInfo": { "form": "visit", "controllerName": "List", "methodName": "GetReferringPhysicians", "displayProperty": "description", "enableTooltip": true, "sortCollectionDataInfo": { "field": "description" } } }, "labelTranslationKey": "ReferringPhysician", "layout": { "row": 4, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "examNumber", "value": "", "type": "string" }, "labelTranslationKey": "ExamNumber", "layout": { "row": 4, "column": 1, "stretch": true } }, { "active": true, "property": { "name": "birthDate", "value": "", "defaultValue": "", "type": "date" }, "labelTranslationKey": "BirthDate", "layout": { "row": 5, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "patientId", "value": "", "type": "string" }, "labelTranslationKey": "PatientId", "layout": { "row": 6, "column": 0, "stretch": true } }, { "active": true, "property": { "name": "otherPatientId", "value": "", "type": "string" }, "labelTranslationKey": "OtherPatientId", "layout": { "row": 6, "column": 1, "stretch": true } } ]',

        const deserializedNreSearchParams: SearchParam[] = JSON.parse(nreSearchParams, this.reviver);

        //EXECUTION
        const deserializedExecutionSearchFilters: SearchParam[] = this.getExecutionFilterFromSettings();

        /* EXECUTIN CUSTOMIZATION (USER) */
        this.params[SearchFormType.EXECUTION] = new SearchParams(deserializedExecutionSearchFilters);

        // DUMMY
        //const deserializedDummyVnext2SearchParams: SearchParam[] = JSON.parse(executionSearchFiltersDef, this.reviver);
        const deserializedDummyVnext2SearchParams: SearchParam[] = deserializedExecutionSearchFilters;

        // SPECIALTY
        let deserializedSpecialtySearchParams: SearchParam[] = JSON.parse(
            settings.specialtyWorklistSettings.searchParamDefs,
            this.reviver,
        );
        const specialtyFavorites =
            userSettings?.preferences.searchGridUserPreference?.specialtyWorklistSearchGrid?.collection ?? [];
        const specialtyFilterValues = this.getFavoriteFilterValues(specialtyFavorites);
        deserializedSpecialtySearchParams = this.loadFavoriteFilterValues(
            specialtyFilterValues,
            deserializedSpecialtySearchParams,
        );

        /* SEARCH PARAMS */
        this.params[SearchFormType.ECG] = new SearchParams(deserializedEcgSearchParams);
        this.params[SearchFormType.VISIT] = new SearchParams(deserializedVisitSearchParams);
        this.params[SearchFormType.ON_PRINTING_CD] = new SearchParams(deserializedOnPrintingCdSearchParams);
        this.params[SearchFormType.COMPLETE_CD] = new SearchParams(deserializedCompleteCdSearchParams);
        this.params[SearchFormType.TELEMONITORING] = new SearchParams(deserializedTelemonitoringSearchParams);
        this.params[SearchFormType.NRE] = new SearchParams(deserializedNreSearchParams);
        this.params[SearchFormType.DUMMY_VNEXT2] = new SearchParams(deserializedDummyVnext2SearchParams);
        this.params[SearchFormType.SPECIALTY] = new SearchParams(deserializedSpecialtySearchParams);
    }

    /**
     * Questo metodo recupera la parte di setting per sito, quindi la parte di settings per utente e
     * costruisce il settings completo composto da i valori presenti nelle due parti in questione
     * @returns Settings completo
     */
    getExecutionFilterFromSettings(): SearchParam[] {
        let isOnError: boolean = false;

        const userSettings: UserConfiguration | null = this.userConfigurationSettingsService.userConfiguration;
        try {
            // Parte di setting utente (specializzato se esiste)
            const executionUserSearchFilters: SearchParam[] = JSON.parse(
                userSettings?.preferences.adminPagesUserPreference?.execution?.filters ?? '[]',
                this.reviver,
            );
            return this.mergeExecutionFilterSettings(executionUserSearchFilters, isOnError);
        } catch (err) {
            console.log(err);
            isOnError = true;
            return [];
        }
    }

    /**
     * Questo metodo recupera la parte di settings per sito e la parte di settings utente
     * cablata nel codice (quella che possiamo chiamare valore di fabbrica). Quindi li unisce.
     * @returns Settings conpleto con valori di fabbrica
     */
    getDefaultSiteExecutionFilter(): SearchParam[] {
        // Valori di fabbrica della parte di setting specializzabile per utente
        const executionUserSearchFilters: SearchParam[] = executionUserFilterDefs();

        return this.mergeExecutionFilterSettings(executionUserSearchFilters);
    }

    /**
     * Questo metodo esegue l'unione tra la parte di setting NON specializzabile per utente
     * ovvero la parte di setting per sito e la parte di setting utente passata dall'esterno
     * @param executionUserSearchFilters parte di setting specializzabile per utente
     * @returns unione tra il settings per sito e quello passato dall'esterno
     */
    mergeExecutionFilterSettings(executionUserSearchFilters: SearchParam[], isOnError: boolean = false): SearchParam[] {
        let executionSiteSearchFilters: SearchParam[] = [];
        try {
            // Parte del setting NON specializzabile per utente
            const settings = this.radioSettingsService.radioSettings;

            executionSiteSearchFilters = JSON.parse(settings.executionWorklistSettings.searchParamDefs, this.reviver);
        } catch (err) {
            console.log(err);
            isOnError = true;
        }

        // Se è in stato di errore (passato dall'esterno o avvenuto qui), uso il default
        if (isOnError) {
            executionUserSearchFilters = executionUserFilterDefs();
            executionSiteSearchFilters = executionSiteFilterDefs();
        }

        let executionSearchFilters: SearchParam[] = [];

        for (let userSetting of executionUserSearchFilters) {
            let siteSetting = executionSiteSearchFilters.find(
                (_siteSetting) => _siteSetting.property.name === userSetting.property.name,
            );

            executionSearchFilters.push({
                ...userSetting,
                ...siteSetting,
                property: {
                    ...userSetting.property,
                    ...siteSetting?.property,
                },
            });
        }

        return executionSearchFilters;
    }

    private reviver(key: string, value: { from: string | Date | undefined; to: string | Date | undefined }) {
        if (key == 'value' && !!value) {
            return paramsDateReviver(value);
        }
        return value;
    }
}
